import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance, { checkTokenValidity, CheckUserExpiry, getUserDetails, hasScientistPondUIAccess,
  handleLegacySessionExpired
} from "../../components/content/axiosConfig";
import Swal from 'sweetalert2';
import CustomModal from "../../components/custom/customModal";
import Preloader from "../../components/custom/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast";


const PondFishes = () => {
  const [samples, setSamples] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [ponds, setPonds] = useState([]);
  const [fishTypes, setFishTypes] = useState([]);
  const userDetails = getUserDetails();
  const fisherID = userDetails.FisherID;
  const canManagePondFish = hasScientistPondUIAccess(userDetails);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [entriesPerPage] = useState(10);
  const [pondName, setPondName] = useState('');
  const [fishType, setFishType] = useState(0);
  const [numberOfFishes, setNumberOfFishes] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [pondFishType, setPondFishType] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [imageToShow, setImageToShow] = useState("");
  const [modalVisible, setModalVisible] = useState(false);

  const openModal2 = (PondDFishID) => {
    setPondFishType(PondDFishID);
    setIsModalOpen(true);
  };

  const closeModal2 = () => setIsModalOpen(false);

  const handleFileChange2 = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = () => setPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    setIsLoading(true);
    const formData = new FormData();
    if (selectedImage) {
      formData.append('image', selectedImage);
    }
    try {
      await axiosInstance.post(`/api/PondFishes/AddFishWithImage/${pondFishType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      showSuccessToast('Image uploaded successfully');
      setIsModalOpen(false);
      setIsLoading(false);
      FetchSamples();
    } catch (error) {
      setIsLoading(false);
      if (error.response && error.response.status === 401) {
        const auth = handleLegacySessionExpired(error);
        showErrorToast(auth.message);
      } else {
        const errorMessage = error.response?.data?.message || "Failed to upload image";
        showErrorToast(errorMessage);
      }
      console.error("Error uploading image:", error);
    }
    setIsModalOpen(false);
  };

  const FetchSamples = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get(`/api/SamplingFishes/GetAllFishes/${fisherID}`, {
        params: { search: searchTerm, page: currentPage, limit: entriesPerPage }
      });
      const body = response.data;
      if (Array.isArray(body)) {
        setSamples(body);
        setTotalCount(body.length);
      } else {
        setSamples(body.data || []);
        setTotalCount(Number(body.total) || 0);
      }
      setIsLoading(false);

    } catch (error) {
      setIsLoading(false);
      if (error.response && error.response.status === 401) {
        const auth = handleLegacySessionExpired(error);
        showErrorToast(auth.message);
      } else {
        const errorMessage = error.response?.data?.message || "Failed to fetch pond fishes";
        showErrorToast(errorMessage);
      }
      console.error("Error fetching pond fishes:", error);
    }
  }, [fisherID, searchTerm, currentPage, entriesPerPage]);

  const FetchPond = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get(`api/PondDetail/GetAllDetails/${fisherID}`, {
        params: { search: '' }
      });
      const users = response.data;
      setPonds(users);
      setIsLoading(false);

    } catch (error) {
      setIsLoading(false);
      if (error.response && error.response.status === 401) {
        const auth = handleLegacySessionExpired(error);
        showErrorToast(auth.message);
      } else {
        const errorMessage = error.response?.data?.message || "Failed to fetch ponds";
        showErrorToast(errorMessage);
      }
      console.error("Error fetching ponds:", error);
    }
  }, [fisherID]);

  const FetchFishTypes = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get(`/api/FishTypes/GetAllFishTypes`, {
        params: { search: '' }
      });
      const users = response.data;
      setFishTypes(users);
      setIsLoading(false);

    } catch (error) {
      setIsLoading(false);
      if (error.response && error.response.status === 401) {
        const auth = handleLegacySessionExpired(error);
        showErrorToast(auth.message);
      } else {
        const errorMessage = error.response?.data?.message || "Failed to fetch fish types";
        showErrorToast(errorMessage);
      }
      console.error("Error fetching fish types:", error);
    }
  }, []);

  const CheckExpiry = useCallback(async () => {
    await CheckUserExpiry();
  }, []);

  useEffect(() => {
    checkTokenValidity();
    CheckExpiry();
    FetchPond();
    FetchFishTypes();
  }, [FetchFishTypes, FetchPond, CheckExpiry]);

  useEffect(() => {
    const delay = searchTerm ? 800 : 0;
    const id = setTimeout(() => FetchSamples(), delay);
    return () => clearTimeout(id);
  }, [FetchSamples, searchTerm, currentPage]);

  const indexOfFirstEntry = totalCount === 0 ? 0 : (currentPage - 1) * entriesPerPage + 1;
  const indexOfLastEntry = Math.min(currentPage * entriesPerPage, totalCount);
  const currentEntries = samples;
  const totalPages = Math.ceil(totalCount / entriesPerPage) || 1;

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const DeleteUser = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      setIsLoading(true);
      try {
        await axiosInstance.delete(`/api/SamplingFishes/DeleteFish/${id}`);
        showSuccessToast("Pond fish deleted successfully");
        await FetchSamples();
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        if (error.response && error.response.status === 401) {
        const auth = handleLegacySessionExpired(error);
        showErrorToast(auth.message);
      } else {
          const errorMessage = error.response?.data?.message || "Failed to delete pond fish";
          showErrorToast(errorMessage);
        }
        console.error("Error deleting pond fish:", error);
      }
    }
  };

  const openModal = (data = null) => {
    if (data) {
      console.log(data);
      setIsEditing(true);
      setEditId(data.PondFishesID);
      setPondName(data.PondDID);
      setFishType(data.FishTypeID);
      setNumberOfFishes(data.NumberOfFishes);
    } else {
      setIsEditing(false);
      setEditId(null);
      setPondName("0");
      setFishType("0");
      setNumberOfFishes(0);
    }
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
  };

  const showImage = (blobData) => {
    setModalVisible(true);
    const imageData = blobData.data;
    const base64Image = `data:image/jpeg;base64,${Buffer.from(imageData).toString("base64")}`;
    setImageToShow(base64Image);
  };

  const handleSubmit = async (e) => {
    if (canManagePondFish) {
      e.preventDefault();
      setIsLoading(true);
      try {
        // Validation for required fields
        if (!pondName || pondName === "0") {
          showErrorToast("Please select a pond detail");
          setIsLoading(false);
          return;
        }
        if (!fishType || fishType === "0") {
          showErrorToast("Please select a fish type");
          setIsLoading(false);
          return;
        }
        if (numberOfFishes < 0) {
          showErrorToast("Number of fishes cannot be negative");
          setIsLoading(false);
          return;
        }

        if (isEditing) {
          await axiosInstance.put(`/api/PondFishes/UpdateFish/${editId}`, {
            PondDID: pondName,
            FishTypeID: fishType,
            FisherID: fisherID,
            NumberOfFishes: numberOfFishes,
            AverageLength: 0,
            StandardDeviationLength: 0,
            AverageWeight: 0,
            StandardDeviationWeight: 0,
            AverageWr: 0,
            StandardDeviationWr: 0,
            NumberOfFishesReleased: 0,
            NumberOfFishesCulled: 0
          });
          showSuccessToast("Pond fish updated successfully");
        } else {
          await axiosInstance.post(`/api/PondFishes/AddFish`, {
            PondDID: pondName,
            FishTypeID: fishType,
            FisherID: fisherID,
            NumberOfFishes: numberOfFishes,
            AverageLength: 0,
            StandardDeviationLength: 0,
            AverageWeight: 0,
            StandardDeviationWeight: 0,
            AverageWr: 0,
            StandardDeviationWr: 0,
            NumberOfFishesReleased: 0,
            NumberOfFishesCulled: 0
          });
          showSuccessToast("Pond fish added successfully");
        }
        FetchSamples();
        setIsLoading(false);
        closeModal();
      } catch (error) {
        setIsLoading(false);
        if (error.response && error.response.status === 401) {
        const auth = handleLegacySessionExpired(error);
        showErrorToast(auth.message);
      } else {
          const errorMessage = error.response?.data?.message || "Failed to save pond fish";
          showErrorToast(errorMessage);
        }
        console.error("Error submitting form:", error);
      }
    }
  };

  return (
    isLoading ? <Preloader /> :
      <div>
        <div className="d-flex align-items-center mb-3">
          <div>
            <ul className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/">Home</Link>
              </li>
              <li className="breadcrumb-item Active"> Pond Fish </li>
            </ul>
            <h1 className="page-header mb-0"> Pond Fish</h1>
          </div>
          <div className="ms-auto">
            {
              canManagePondFish ?
                <button
                  type="button"
                  className="btn btn-success btn-rounded px-4 rounded-pill"
                  onClick={() => openModal()}
                >
                  <i className="fa fa-plus fa-lg me-2 ms-n2 text-success-900"></i>
                  Add Pond Fish
                </button> : <div></div>}
          </div>
        </div>
        <div className="card border-0">
          <div className="tab-content p-3">
            <div className="tab-pane fade show active" id="allTab">
              <div className="input-group mb-3">
                <div className="flex-fill position-relative">
                  <div className="input-group">
                    <div
                      className="input-group-text position-absolute top-0 bottom-0 bg-none border-0 start-0"
                      style={{ zIndex: 10 }}
                    >
                      <i className="fa fa-search opacity-5"></i>
                    </div>
                    <input
                      type="text"
                      className="form-control px-35px bg-light"
                      placeholder="Search Sample..."
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="table-responsive mb-3">
                <table className="table table-hover table-panel text-nowrap align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Pond Name</th>
                      <th>Pond Fish Type </th>
                      <th>Number OF Fishes</th>
                      {/* <th>Avg Length</th>
                      <th>Stand Dev Length</th>
                      <th>Avg Weight</th>
                      <th>Stand Dev Weight</th>
                      <th>Avg Wr</th>
                      <th>Stand Dev Wr</th>
                      <th>Culled</th>
                      <th>Released</th> */}
                      <th>Actions </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentEntries.map((web) => (
                      <tr key={web.PondFishesID}>
                        <td>{web.PondName}</td>
                        <td>{web.FishTypeName}</td>
                        <td>{web.NumberOfFishes}</td>
                        {/* <td>{web.AverageLength}</td>
                        <td>{web.StandardDeviationLength}</td>
                        <td>{web.AverageWeight}</td>
                        <td>{web.StandardDeviationWeight}</td>
                        <td>{web.AverageWr}</td>
                        <td>{web.StandardDeviationWr}</td>
                        <td>{web.NumberOfFishesCulled}</td>
                        <td>{web.NumberOfFishesReleased}</td> */}
                        <td>
                          {web.imagePath ? (
                            <span
                              onClick={() => showImage(web.imagePath)} // Function to display the image
                              style={{ cursor: "pointer" }}
                            >
                              <i className="fa fa-eye fa-lg me-2 ms-n2 text-danger-900"></i>
                            </span>
                          ) : null}
                          &nbsp;&nbsp;
                          {canManagePondFish && (
                            <div className="donezo-row-actions">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                title="Upload image"
                                aria-label="Upload image"
                                onClick={() => openModal2(web.PondFishesID)}
                              >
                                <i className="fa fa-upload" aria-hidden />
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                title="Edit fish"
                                aria-label="Edit fish"
                                onClick={() => openModal(web)}
                              >
                                <i className="fa fa-pencil" aria-hidden />
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-danger"
                                title="Delete fish"
                                aria-label="Delete fish"
                                onClick={() => DeleteUser(web.PondFishesID)}
                              >
                                <i className="fa fa-trash" aria-hidden />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="d-md-flex align-items-center">
                <div className="me-md-auto text-md-left text-center mb-2 mb-md-0">
                  Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalCount} entries
                </div>
                <ul className="pagination mb-0 justify-content-center">
                  <li
                    className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(currentPage - 1)}
                    >
                      Previous
                    </button>
                  </li>
                  {[...Array(totalPages)].map((_, index) => (
                    <li
                      key={index}
                      className={`page-item ${index + 1 === currentPage ? "active" : ""
                        }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(index + 1)}
                      >
                        {index + 1}
                      </button>
                    </li>
                  ))}
                  <li
                    className={`page-item ${currentPage === totalPages ? "disabled" : ""
                      }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(currentPage + 1)}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>


        <CustomModal
          isOpen={modalVisible}
          onClose={() => setModalVisible(false)}
          title="Fish Image"
          showButton={false}
          modalSize="modal-lg"
          minHeight="600px"
        >
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#fff",
            }}
          >
            <img
              src={imageToShow}
              alt="Fish"
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          </div>
        </CustomModal>

        <CustomModal
          isOpen={isModalOpen}
          onClose={closeModal2}
          title="Select Image"
          showButton={false}
          modalSize="modal-lg"
          minHeight="600px"
        >
          <span className="text-danger">(Size Should be Lower than 4MB)</span>
          <div className="mt-1">
            <input type="file" accept="image/*" onChange={handleFileChange2} />
            {preview && (
              <div className="row pt-3">
                <img
                  src={preview}
                  alt="Selected Preview"
                  style={{
                    width: "100%",
                    maxHeight: "400px",
                    objectFit: "contain",
                  }}
                />
              </div>
            )}
          </div>

          <div className="row pt-4">
            <div className="col-md-12 text-center">
              <button
                style={{ width: "50%" }}
                onClick={handleUpload}
                className="btn btn-success"
              >
                Upload
              </button>
            </div>
          </div>
        </CustomModal>

        <CustomModal
          isOpen={modalIsOpen}
          onClose={closeModal}
          title={isEditing ? "Edit Pond Fish" : "Add Pond Fish"}
          showButton={true}
          modalSize="modal-lg"
          minHeight="500px"
          onSubmit={handleSubmit}
        >
          <div className="card border-0">
            <div className="tab-content p-3">
              <div className="tab-pane fade show active" id="allTab">
                <div className="form-floating mb-25px">
                  <select
                    className="form-control h-50px fs-13px"
                    id="PondName"
                    name="PondName"
                    disabled={isEditing}
                    value={pondName}
                    onChange={(e) => setPondName(e.target.value)}
                  >
                    <option value="0">Select Pond Detail</option>
                    {ponds.map((web) => (
                      <option key={web.id} value={web.id}>
                        {web.Name}
                      </option>
                    ))}
                  </select>
                  <label
                    htmlFor="PondName"
                    className="d-flex align-items-center fs-13px text-gray-600"
                  >
                    Pond Detail &nbsp; <span>*</span>
                  </label>
                </div>

                <div className="form-floating mb-25px">
                  <select
                    className="form-control h-50px fs-13px"
                    id="FishType"
                    name="FishType"
                    value={fishType}
                    disabled={isEditing}
                    onChange={(e) => setFishType(e.target.value)}
                  >
                    <option value="0">Select Fish Type</option>
                    {fishTypes.map((web) => (
                      <option key={web.id} value={web.id}>
                        {web.Name}
                      </option>
                    ))}
                  </select>
                  <label
                    htmlFor="FishType"
                    className="d-flex align-items-center fs-13px text-gray-600"
                  >
                    Fish Type &nbsp; <span>*</span>
                  </label>
                </div>

                <div className="form-floating mb-15px">
                  <input
                    type="number"
                    step={1}
                    className="form-control h-45px fs-13px"
                    placeholder="Length"
                    id="Length"
                    name="Length"
                    value={numberOfFishes}
                    onChange={(e) => setNumberOfFishes(e.target.value)}
                  />
                  <label
                    htmlFor="Length"
                    className="d-flex align-items-center fs-13px text-gray-600"
                  >
                    Number of Fishes
                  </label>
                </div>
              </div>
            </div>
          </div>

        </CustomModal>



      </div>
  );
};

export default PondFishes;